import React, { useEffect, useState } from 'react';
import api from '../services/api';
import {
  FileText, Download, FileSpreadsheet, Printer, FolderKanban,
  Package, CheckCircle, Camera, Calendar, Loader
} from 'lucide-react';

export default function Reports() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/projects').then(r => {
      const data = r.data.data || r.data || [];
      setProjects(data);
      if (data.length > 0) setSelectedProject(data[0].id);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const downloadReport = async (type, url, filename) => {
    setDownloading(type);
    try {
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Download error:', err);
      alert('Error al descargar el reporte. Verifica que el proyecto tiene datos.');
    } finally {
      setDownloading(null);
    }
  };

  const selectedProj = projects.find(p => p.id === selectedProject);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const reportTypes = [
    {
      id: 'project-pdf',
      title: 'Informe de Avance de Obra',
      description: 'Reporte completo en PDF con resumen del proyecto, tareas, hitos contractuales y equipos. Ideal para presentar a clientes y dirección.',
      icon: <FileText size={24} />,
      color: 'red',
      format: 'PDF',
      action: () => {
        const code = selectedProj?.code || 'proyecto';
        downloadReport('project-pdf',
          `/reports/project/${selectedProject}/pdf`,
          `informe-avance-${code}-${today()}.pdf`
        );
      },
      requiresProject: true,
    },
    {
      id: 'project-excel',
      title: 'Reporte Completo de Proyecto',
      description: 'Excel con múltiples hojas: resumen, tareas, hitos, equipos y fotos. Incluye todos los datos para análisis detallado.',
      icon: <FileSpreadsheet size={24} />,
      color: 'green',
      format: 'XLSX',
      action: () => {
        const code = selectedProj?.code || 'proyecto';
        downloadReport('project-excel',
          `/reports/project/${selectedProject}/excel`,
          `reporte-${code}-${today()}.xlsx`
        );
      },
      requiresProject: true,
    },
    {
      id: 'inventory-excel',
      title: 'Inventario General de Equipos',
      description: 'Excel con todos los equipos de todos los proyectos. Incluye tipo, marca, modelo, número de serie, estado y ubicación.',
      icon: <Package size={24} />,
      color: 'purple',
      format: 'XLSX',
      action: () => {
        downloadReport('inventory-excel',
          '/reports/inventory/excel',
          `inventario-general-${today()}.xlsx`
        );
      },
      requiresProject: false,
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Reportes</h1>
          <p className="subtitle">Genera informes en PDF y Excel para descargar</p>
        </div>
      </div>

      {/* Project Selector */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h3><FolderKanban size={18} /> Seleccionar proyecto</h3>
        </div>
        <div className="card-body">
          <select className="filter-select" value={selectedProject}
            onChange={e => setSelectedProject(e.target.value)}
            style={{ maxWidth: 500 }}>
            {projects.map(p => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.name} ({Math.round(p.progress_percentage || 0)}%)
              </option>
            ))}
          </select>

          {selectedProj && (
            <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
              <InfoChip icon={<CheckCircle size={14} />} label="Estado" value={statusLabel(selectedProj.status)} />
              <InfoChip icon={<Calendar size={14} />} label="Progreso" value={`${Math.round(selectedProj.progress_percentage || 0)}%`} />
              <InfoChip icon={<Camera size={14} />} label="Capacidad" value={`${selectedProj.plant_capacity_kwp || 0} kWp`} />
            </div>
          )}
        </div>
      </div>

      {/* Report Cards */}
      <div className="report-grid">
        {reportTypes.map(report => (
          <div key={report.id} className="card report-card">
            <div className="report-card-header">
              <div className={`report-icon ${report.color}`}>{report.icon}</div>
              <span className={`report-format ${report.color}`}>{report.format}</span>
            </div>
            <h3 className="report-title">{report.title}</h3>
            <p className="report-desc">{report.description}</p>
            <button
              className="btn btn-primary btn-full"
              onClick={report.action}
              disabled={downloading === report.id || (report.requiresProject && !selectedProject)}
            >
              {downloading === report.id ? (
                <><Loader size={16} className="spin" /> Generando...</>
              ) : (
                <><Download size={16} /> Descargar {report.format}</>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Quick Tips */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header">
          <h3><Printer size={18} /> Consejos</h3>
        </div>
        <div className="card-body" style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          <p style={{ marginBottom: 8 }}>• El <strong>Informe PDF</strong> es ideal para enviar a clientes y dirección. Incluye cabecera con marca ENERGESTICS y tablas de progreso.</p>
          <p style={{ marginBottom: 8 }}>• El <strong>Reporte Excel</strong> tiene hojas separadas para tareas, hitos, equipos y fotos — perfecto para análisis detallado.</p>
          <p>• El <strong>Inventario</strong> agrupa todos los equipos de todos los proyectos en un solo archivo, útil para control de stock.</p>
        </div>
      </div>
    </div>
  );
}

function InfoChip({ icon, label, value }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: 12, color: 'var(--text-secondary)',
      background: 'var(--bg-input)', padding: '4px 10px', borderRadius: 6,
    }}>
      {icon} {label}: <strong style={{ color: 'var(--text-primary)' }}>{value}</strong>
    </span>
  );
}

function statusLabel(s) {
  return { draft: 'Borrador', in_progress: 'En progreso', completed: 'Completado',
    on_hold: 'En espera', cancelled: 'Cancelado' }[s] || s || '-';
}

function today() {
  return new Date().toISOString().split('T')[0];
}
